import React, {
  forwardRef,
  Ref,
  ReactElement,
  useContext,
  useState,
} from 'react';
import {
  Dialog,
  Button,
  Stack,
  Typography,
  Slide,
  useMediaQuery,
  useTheme,
  Card,
  Grid,
  Box,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import { resendRegistrationEmail } from 'api';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import { User } from 'types';
import { useTranslation } from 'react-i18next';
import {
  getFormattedUserName,
  mobileCardSx,
  cardSx,
  cardHeaderSx,
} from 'utils';
import { TransitionProps } from '@mui/material/transitions';
import { ValidatorForm } from 'react-material-ui-form-validator';
import AuturaTextField from 'components/inputs/AuturaTextField';
interface ResendRegistrationEmailDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  user: User;
}

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement<any, any>;
  },
  ref: Ref<unknown>,
) {
  return <Slide direction='left' ref={ref} {...props} />;
});

const ResendEmailDialog = ({
  isOpen,
  handleClose,
  user,
}: ResendRegistrationEmailDialogProps) => {
  const { t } = useTranslation();
  const displayToast = useContext(ToastContext);
  const [email, setEmail] = useState<string>(user.contact?.primaryEmail || '');
  const [sending, setSending] = useState<boolean>(false);
  const { palette, breakpoints } = useTheme();
  const mobile = useMediaQuery(breakpoints.down('lg'));

  const handleResendRegistrationEmail = async (): Promise<void> => {
    setSending(true);
    const updatedUser = { ...user, email };
    const res = await resendRegistrationEmail(updatedUser);
    if (res.ok) {
      displayToast({
        type: TOAST_TYPE.SUCCESS,
        message: t('registration_sent', {
          ns: 'toasts',
          email: user.email || getFormattedUserName(user),
        }),
      });
      handleClose();
    } else {
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: t('send_error', {
          ns: 'toasts',
          name: t('registration'),
          error: `${res.status} - ${res.statusText}`,
        }),
      });
    }
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      TransitionComponent={Transition}
      aria-label={`${t('labels.resend_registration', {
        ns: 'userSettings',
      })} Dialog`}
      fullScreen={mobile}
      fullWidth
      sx={{ mt: { xs: -1, lg: 0 }, pb: 0 }}
    >
      <ValidatorForm
        onSubmit={handleResendRegistrationEmail}
        instantValidate={false}
      >
        <Card sx={mobile ? mobileCardSx : cardSx}>
          <Box
            sx={
              mobile
                ? {
                    display: 'flex',
                    p: { xs: 2, sm: 4 },
                    backgroundColor: palette.secondary.main,
                  }
                : cardHeaderSx
            }
          >
            <Box sx={{ flex: 1, p: 0.5 }}>
              <Typography
                variant={'h4'}
                sx={{
                  color: palette.secondary.contrastText,
                  textAlign: 'left',
                }}
              >
                {t('labels.resend_registration', { ns: 'userSettings' })}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2 }}>
            <Grid container width={'100%'} spacing={1.5}>
              <Grid size={12}>
                <AuturaTextField
                  autoFocus
                  label={t('email')}
                  name='email'
                  type='email'
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  validators={['isEmail']}
                  errorMessages={['Invalid email address']}
                  required
                />
              </Grid>
              <Grid size={12}>
                <Stack
                  width='100%'
                  direction='row'
                  spacing={2}
                  justifyContent='flex-end'
                  mt={2}
                  display='flex'
                  alignItems='flex-end'
                  alignContent='flex-end'
                >
                  <Button
                    variant={'outlined'}
                    onClick={handleClose}
                    color={'secondary'}
                    endIcon={<CancelIcon />}
                    disabled={sending}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    variant={'contained'}
                    type='submit'
                    color={'secondary'}
                    endIcon={<SendIcon />}
                    onClick={handleResendRegistrationEmail}
                    disabled={sending}
                  >
                    {t('send')}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </ValidatorForm>
    </Dialog>
  );
};

export default ResendEmailDialog;
